/**
 * Compacte tekst/vul-opmaak-balk voor de custom render-paden (Roadmap +
 * Roadmap-matrix). Verschijnt zodra een box geselecteerd is; past font,
 * grootte, font-kleur, vul-kleur, horizontale tekst-uitlijning en verticale
 * tekst-positie toe via een `BoxStyle`-patch.
 *
 * Pictogrammen + controls zijn identiek aan de CanvasToolbar (zelfde
 * `TextAlignIcon`, `SwatchColorPicker` en font-lijsten) zodat de opmaak overal
 * hetzelfde aanvoelt.
 */
import type { BoxStyle } from "@ae/domain";
import { useTranslation } from "react-i18next";
import { SwatchColorPicker } from "./SwatchColorPicker";

// Gelijk aan CanvasToolbar.
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];
const FONT_FAMILIES = [
  { label: "Standaard", value: "" },
  { label: "Sans-serif", value: "sans-serif" },
  { label: "Serif", value: "serif" },
  { label: "Monospace", value: "monospace" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Verdana", value: "Verdana, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Courier New", value: "'Courier New', monospace" },
];

/** Tekst-uitlijn-icoon (links/midden/rechts) — identiek aan CanvasToolbar. */
function TextAlignIcon({ align }: { align: "left" | "center" | "right" }) {
  const short =
    align === "left"
      ? { x1: 2, x2: 10 }
      : align === "right"
        ? { x1: 6, x2: 14 }
        : { x1: 4, x2: 12 };
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      fill="none"
      aria-hidden
    >
      <line x1="2" y1="3.5" x2="14" y2="3.5" />
      <line x1={short.x1} y1="7" x2={short.x2} y2="7" />
      <line x1="2" y1="10.5" x2="14" y2="10.5" />
      <line x1={short.x1} y1="14" x2={short.x2} y2="14" />
    </svg>
  );
}

/** Verticale-positie-icoon (boven/midden/onder) — tekstregels in een box,
 *  geankerd; zelfde stroke-stijl als `TextAlignIcon`. */
function VAlignIcon({ align }: { align: "top" | "middle" | "bottom" }) {
  const y = align === "top" ? 3.5 : align === "bottom" ? 9 : 6.25;
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      stroke="currentColor"
      strokeLinecap="round"
      fill="none"
      aria-hidden
    >
      <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" strokeWidth="1" opacity="0.4" />
      <line x1="4.5" y1={y} x2="11.5" y2={y} strokeWidth="1.6" />
      <line x1="4.5" y1={y + 2.5} x2="9.5" y2={y + 2.5} strokeWidth="1.6" />
    </svg>
  );
}

export function BoxFormatToolbar({
  style,
  onChange,
  onClose,
}: {
  style: BoxStyle;
  onChange: (patch: BoxStyle) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const btn = (active: boolean) =>
    `flex h-7 w-7 items-center justify-center rounded ${
      active ? "bg-blue-100 text-blue-700" : "text-slate-600 hover:bg-slate-100"
    }`;
  return (
    <div className="-translate-x-1/2 absolute top-2 left-1/2 z-20 flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-1 shadow-lg">
      <select
        value={style.fontFamily ?? ""}
        onChange={(e) => onChange({ fontFamily: e.target.value || undefined })}
        title={t("boxFormat.font")}
        className="rounded border border-slate-300 px-1 py-0.5 text-slate-700 text-xs"
      >
        {FONT_FAMILIES.map((f) => (
          <option key={f.value} value={f.value}>
            {f.value === "" ? t("ribbon.default") : f.label}
          </option>
        ))}
      </select>
      <select
        value={style.fontSize ?? ""}
        onChange={(e) =>
          onChange({ fontSize: e.target.value ? Number(e.target.value) : undefined })
        }
        title={t("boxFormat.size")}
        className="rounded border border-slate-300 px-1 py-0.5 text-slate-700 text-xs"
      >
        <option value="">–</option>
        {FONT_SIZES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      {/* Tekstkleur — identieke trigger als CanvasToolbar (A + gekleurde streep). */}
      <SwatchColorPicker
        title={t("boxFormat.fontColor")}
        value={style.fontColor}
        allowClear
        onChange={(c) => onChange({ fontColor: c ?? undefined })}
        renderTrigger={({ color, onClick, open }) => (
          <button
            type="button"
            title={t("boxFormat.fontColor")}
            onClick={onClick}
            className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-black text-xs hover:bg-slate-100 ${
              open ? "bg-slate-100" : ""
            }`}
          >
            <span className="select-none">A</span>
            <span
              className="absolute right-1 bottom-1 left-1 h-0.5 rounded"
              style={{ backgroundColor: color ?? "#334155" }}
            />
          </button>
        )}
      />
      {/* Vulkleur — identieke trigger (gekleurd vierkant). */}
      <SwatchColorPicker
        title={t("boxFormat.fillColor")}
        value={style.fillColor}
        allowClear
        allowTransparent
        onChange={(c) => onChange({ fillColor: c ?? undefined })}
        renderTrigger={({ color, onClick, open }) => (
          <button
            type="button"
            title={t("boxFormat.fillColor")}
            onClick={onClick}
            className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-xs hover:bg-slate-100 ${
              open ? "bg-slate-100" : ""
            }`}
          >
            <span
              className="h-4 w-4 rounded border border-slate-300"
              style={{ backgroundColor: color ?? "#ffffff" }}
            />
          </button>
        )}
      />

      <span className="h-5 w-px bg-slate-200" />
      <div className="flex gap-0.5">
        {(["left", "center", "right"] as const).map((a) => (
          <button
            key={a}
            type="button"
            className={btn(style.textAlign === a)}
            title={t(`boxFormat.align.${a}`)}
            onClick={() => onChange({ textAlign: a })}
          >
            <TextAlignIcon align={a} />
          </button>
        ))}
      </div>
      <span className="h-5 w-px bg-slate-200" />
      <div className="flex gap-0.5">
        {(["top", "middle", "bottom"] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={btn(style.verticalAlign === v)}
            title={t(`boxFormat.vAlign.${v}`)}
            onClick={() => onChange({ verticalAlign: v })}
          >
            <VAlignIcon align={v} />
          </button>
        ))}
      </div>
      <span className="h-5 w-px bg-slate-200" />
      <button
        type="button"
        className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        title={t("boxFormat.clear")}
        onClick={() =>
          onChange({
            fontFamily: undefined,
            fontSize: undefined,
            fontColor: undefined,
            fillColor: undefined,
            textAlign: undefined,
            verticalAlign: undefined,
          })
        }
      >
        <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden>
          <path d="M6.5 3 2 7.5a1.5 1.5 0 0 0 0 2.1L4.4 12H13v-1.5H8.1l5-5a1.5 1.5 0 0 0 0-2.1L10.6 3a1.5 1.5 0 0 0-2.1 0Zm1 1 2.4 2.5-2.9 2.9L4.5 6.9 7.5 4Z" />
        </svg>
      </button>
      <button
        type="button"
        className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        title={t("boxFormat.close")}
        onClick={onClose}
      >
        <svg
          viewBox="0 0 16 16"
          width="13"
          height="13"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          aria-hidden
        >
          <line x1="4" y1="4" x2="12" y2="12" />
          <line x1="12" y1="4" x2="4" y2="12" />
        </svg>
      </button>
    </div>
  );
}
